<script setup lang="ts">
import { computed } from 'vue';
import UiText from './UiText.vue';
import UiNavLink from './UiNavLink.vue';
import UiIcon from './UiIcon.vue';
import { isExternalHref } from '../utils/isExternalHref';

interface UiLinkProps {
    label: string;
    href: string;
    theme?: 'dark' | 'light';
    variant?: 'p' | 'p-small' | 'caption' | 'button' | 'h6' | 'h5';
    hideLabel?: boolean;
    overflowEllipsis?: boolean;
    external?: 'subtle' | 'with-arrow' | 'none';
    follow?: boolean;
    allowReferrer?: boolean;
}

const props = withDefaults(defineProps<UiLinkProps>(), {
    href: '',
    theme: 'light',
    variant: 'p',
    hideLabel: false,
    follow: false,
});

const target = computed(() => props.external === 'none' ? '_self' : '_blank');


const isExternal = computed(() => {
    if (props.external === 'none') return false;
    return isExternalHref(props.href);
});

const isShopexpertsUrl = computed(() => {
    if (!/^https?:\/\//i.test(props.href)) return false;

    try {
        const hostname = new URL(props.href).hostname;
        return hostname === 'shopexperts.com' || hostname.endsWith('.shopexperts.com');
    } catch {
        return false;
    }
});

const rel = computed(() => {
    if (!isExternal.value || props.follow) {
        return undefined;
    }
    return props.allowReferrer || isShopexpertsUrl.value ? 'noopener nofollow' : 'noopener noreferrer nofollow';
});
</script>

<template>
    <UiNavLink :target="target" :to="isExternal ? undefined : href" :external="isExternal" :href="props.href" :rel="rel"
        :allow-referrer="props.allowReferrer"
        :class="props.theme === 'dark' ? 'text-se-white hover:text-se-primary' : 'text-se-black hover:text-se-dark'">
        <slot />
        <div class="flex items-center gap-1">
            <UiText :text="label" as="span" :variant="props.variant" v-if="!props.hideLabel" :class="{
                'truncate block max-w-full overflow-hidden': props.overflowEllipsis,
                'text-se-highlight hover:underline': props.external === 'with-arrow',
            }" no-wrap />
            <UiIcon v-if="props.external === 'with-arrow'" name="link-external" :class="{
                'w-3.5 h-3.5': props.variant !== 'h6',
                'w-2.5 h-2.5': props.variant === 'h6',
            }" />
        </div>
    </UiNavLink>
</template>
